<template>
  <main class="collections-index-page">
    <header class="index-header">
      <div>
        <router-link :to="{ name: 'ceremonySearch' }" class="back-link">
          ← Ceremony Intelligence
        </router-link>
        <p class="eyebrow">Collections</p>
        <h2>Your saved highlights</h2>
        <span>
          Return to a shortlist, prepare its clips, or download work that is ready.
        </span>
      </div>
      <router-link :to="{ name: 'ceremonySearch' }" class="explore-link">
        Explore ceremonies
      </router-link>
    </header>

    <section v-if="loading" class="index-state" aria-live="polite">
      <div class="state-mark"></div>
      <p>Loading your collections…</p>
    </section>

    <section v-else-if="loadError" class="index-state error-state" role="alert">
      <h3>Your collections could not be loaded</h3>
      <p>{{ loadError }}</p>
      <button type="button" @click="load">Try again</button>
    </section>

    <section v-else-if="!collections.length" class="index-state empty-state">
      <span aria-hidden="true">✦</span>
      <h3>Start your first collection</h3>
      <p>
        Collections are created when you save a useful moment from a ceremony search.
      </p>
      <router-link :to="{ name: 'ceremonySearch' }">Explore ceremonies</router-link>
    </section>

    <section v-else aria-labelledby="collection-list-heading">
      <div class="list-heading">
        <h3 id="collection-list-heading">
          {{ collections.length }} collection{{ collections.length === 1 ? '' : 's' }}
        </h3>
        <p>Most recently updated first</p>
      </div>

      <div class="collection-grid">
        <article
          v-for="collection in orderedCollections"
          :key="collection.collection_id"
          class="collection-card"
          :class="{ 'is-editing': editingCollectionId === collection.collection_id }"
        >
          <div class="card-topline">
            <span class="status-pill" :class="`is-${statusFor(collection).tone}`">
              {{ statusFor(collection).label }}
            </span>
            <div class="card-topline-actions">
              <time v-if="collection.updated_at" :datetime="collection.updated_at">
                {{ formatUpdatedAt(collection.updated_at) }}
              </time>
              <button
                v-if="editingCollectionId !== collection.collection_id"
                type="button"
                class="card-rename-trigger"
                :aria-label="`Rename ${collection.title || 'collection'}`"
                title="Rename collection"
                @click="startRename(collection)"
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M13.5 6.5 17.5 10.5M4 20l3.8-.8L19 8a2.1 2.1 0 0 0-3-3L4.8 16.2 4 20Z" />
                </svg>
              </button>
            </div>
          </div>

          <form
            v-if="editingCollectionId === collection.collection_id"
            class="card-rename-form"
            @submit.prevent="saveRename(collection)"
          >
            <p>{{ collection.university_name || 'University highlights' }}</p>
            <label :for="`collection-title-${collection.collection_id}`">
              Collection name
            </label>
            <input
              :id="`collection-title-${collection.collection_id}`"
              ref="renameInput"
              v-model="editingTitle"
              maxlength="100"
              :disabled="renamePending"
              @keydown.esc.prevent="cancelRename"
            />
            <p v-if="renameError" class="card-rename-error" role="alert">
              {{ renameError }}
            </p>
            <div class="card-rename-actions">
              <button type="button" :disabled="renamePending" @click="cancelRename">
                Cancel
              </button>
              <button type="submit" :disabled="renamePending || !editingTitle.trim()">
                {{ renamePending ? 'Saving…' : 'Save name' }}
              </button>
            </div>
          </form>

          <router-link
            v-else
            :to="{
              name: 'ceremonyHighlights',
              params: { collectionId: collection.collection_id },
            }"
            class="collection-card-link"
            :aria-label="`Open ${collection.title || 'saved highlights'}`"
          >
            <div class="card-copy">
              <p>{{ collection.university_name || 'University highlights' }}</p>
              <h3>{{ collection.title || 'Saved highlights' }}</h3>
            </div>

            <dl>
              <div>
                <dt>Moments</dt>
                <dd>{{ collection.item_count || 0 }}</dd>
              </div>
              <div>
                <dt>Ceremonies</dt>
                <dd>{{ collection.ceremony_count || 0 }}</dd>
              </div>
            </dl>

            <footer>
              <span>{{ statusFor(collection).detail }}</span>
              <strong>Open collection →</strong>
            </footer>
          </router-link>
        </article>
      </div>
    </section>
  </main>
</template>

<script>
import { trackAdminEvent } from '../../admin/activity.js'
import {
  listHighlightCollections,
  updateHighlightCollection,
} from '../../api/highlightCollections.js'

export default {
  name: 'CeremonyHighlightCollectionsIndex',
  data() {
    return {
      collections: [],
      loading: true,
      loadError: '',
      editingCollectionId: '',
      editingTitle: '',
      renamePending: false,
      renameError: '',
    }
  },
  computed: {
    orderedCollections() {
      return [...this.collections].sort((left, right) => (
        new Date(right.updated_at || 0).getTime()
        - new Date(left.updated_at || 0).getTime()
      ))
    },
  },
  created() {
    this.load()
  },
  methods: {
    startRename(collection) {
      if (!collection?.collection_id || this.renamePending) return
      this.editingCollectionId = collection.collection_id
      this.editingTitle = collection.title || ''
      this.renameError = ''
      this.$nextTick(() => {
        const inputs = this.$refs.renameInput
        const input = Array.isArray(inputs) ? inputs[0] : inputs
        input?.focus()
        input?.select()
      })
    },
    cancelRename() {
      if (this.renamePending) return
      this.editingCollectionId = ''
      this.editingTitle = ''
      this.renameError = ''
    },
    async saveRename(collection) {
      const title = String(this.editingTitle || '').trim()
      if (!collection?.collection_id || !title || this.renamePending) return
      if (title === collection.title) {
        this.cancelRename()
        return
      }
      this.renamePending = true
      this.renameError = ''
      try {
        const response = await updateHighlightCollection(collection.collection_id, { title })
        const index = this.collections.findIndex(
          item => item.collection_id === collection.collection_id
        )
        if (index !== -1) {
          this.$set(this.collections, index, {
            ...this.collections[index],
            ...response.data,
          })
        }
        trackAdminEvent('highlight_collection_renamed', {
          toolKey: 'ceremony_intelligence',
          metadata: { collectionId: collection.collection_id },
        })
        this.editingCollectionId = ''
        this.editingTitle = ''
        this.renameError = ''
      } catch (error) {
        this.renameError = (
          error?.response?.data?.error || 'The collection could not be renamed.'
        )
      } finally {
        this.renamePending = false
      }
    },
    async load() {
      this.loading = true
      this.loadError = ''
      try {
        const response = await listHighlightCollections()
        this.collections = Array.isArray(response.data?.collections)
          ? response.data.collections
          : []
        trackAdminEvent('highlight_collections_opened', {
          toolKey: 'ceremony_intelligence',
          metadata: { collectionCount: this.collections.length },
        })
      } catch (error) {
        this.loadError = (
          error?.response?.data?.error
          || error?.message
          || 'Try again in a moment.'
        )
      } finally {
        this.loading = false
      }
    },
    readyCount(collection) {
      return Number(collection?.ready_count || 0)
    },
    statusFor(collection) {
      const job = collection?.export_job || {}
      const status = String(job.status || '').toUpperCase()
      const itemCount = Number(collection?.item_count || 0)
      const ready = this.readyCount(collection)
      if (['QUEUED', 'RUNNING'].includes(status)) {
        const jobTotal = Number(job.item_count || 0)
        const prepared = Number(job.prepared_count || 0)
        return {
          tone: 'processing',
          label: 'Preparing clips',
          detail: jobTotal
            ? `${prepared} of ${jobTotal} clips prepared in this job.`
            : 'You can return while preparation continues.',
        }
      }
      if (itemCount && ready === itemCount) {
        return {
          tone: 'ready',
          label: 'Ready',
          detail: `${ready} clip${ready === 1 ? '' : 's'} ready to download.`,
        }
      }
      if (ready) {
        return {
          tone: 'attention',
          label: `${ready} of ${itemCount} ready`,
          detail: 'Review the remaining moments and prepare them when ready.',
        }
      }
      if (['ERROR', 'PARTIAL'].includes(status)) {
        return {
          tone: 'attention',
          label: 'Needs attention',
          detail: 'Your saved moments are safe and can be prepared again.',
        }
      }
      return {
        tone: 'draft',
        label: itemCount ? 'Ready to review' : 'Empty',
        detail: itemCount
          ? 'Review the order and trims before preparing clips.'
          : 'Add moments from Ceremony Intelligence.',
      }
    },
    formatUpdatedAt(value) {
      const date = new Date(value)
      if (!Number.isFinite(date.getTime())) return ''
      return `Updated ${new Intl.DateTimeFormat('en-GB', {
        day: 'numeric',
        month: 'short',
      }).format(date)}`
    },
  },
}
</script>

<style scoped>
.collections-index-page {
  max-width: 1120px;
  margin: 0 auto;
  padding: 12px 24px 56px;
}

.index-header {
  display: flex;
  align-items: end;
  justify-content: space-between;
  gap: 28px;
  margin-bottom: 34px;
}

.index-header > div {
  max-width: 700px;
}

.back-link {
  display: inline-flex;
  min-height: 44px;
  align-items: center;
  color: #5f6879;
  font-size: 0.78rem;
  font-weight: 650;
  text-decoration: none;
}

.eyebrow,
.card-copy p {
  margin: 16px 0 6px;
  color: #7c3aed;
  font-size: 0.7rem;
  font-weight: 750;
  letter-spacing: 0.09em;
  text-transform: uppercase;
}

.index-header h2 {
  margin: 0;
  color: #171a25;
  font-size: clamp(1.9rem, 4vw, 2.75rem);
  letter-spacing: -0.04em;
  line-height: 1.06;
}

.index-header > div > span {
  display: block;
  margin-top: 11px;
  color: #6f7787;
  font-size: 0.92rem;
  line-height: 1.55;
}

.explore-link,
.index-state a,
.index-state button {
  display: inline-flex;
  min-height: 46px;
  align-items: center;
  justify-content: center;
  padding: 0 18px;
  border: 0;
  border-radius: 11px;
  background: #6d28d9;
  color: #fff;
  font-size: 0.8rem;
  font-weight: 720;
  text-decoration: none;
  cursor: pointer;
}

.list-heading {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 14px;
}

.list-heading h3,
.list-heading p {
  margin: 0;
}

.list-heading h3 {
  color: #252938;
  font-size: 0.94rem;
}

.list-heading p {
  color: #848b99;
  font-size: 0.72rem;
}

.collection-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.collection-card {
  display: flex;
  flex-direction: column;
  min-height: 260px;
  padding: 21px;
  border: 1px solid #e1e4eb;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 10px 28px rgba(24, 31, 47, 0.05);
  transition: border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease;
}

.collection-card:hover {
  border-color: #cabcf4;
  box-shadow: 0 17px 38px rgba(58, 39, 104, 0.1);
  transform: translateY(-2px);
}

.collection-card:focus-within {
  outline: 3px solid rgba(109, 40, 217, 0.25);
  outline-offset: 3px;
}

.collection-card.is-editing {
  border-color: #c4b5fd;
  box-shadow: 0 17px 38px rgba(58, 39, 104, 0.1);
  outline: none;
  transform: none;
}

.collection-card-link {
  display: grid;
  flex: 1;
  color: inherit;
  text-decoration: none;
}

.collection-card-link:focus-visible {
  outline: none;
}

.card-topline,
.collection-card footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.card-topline time {
  color: #8a91a0;
  font-size: 0.68rem;
}

.card-topline-actions {
  display: flex;
  align-items: center;
  gap: 9px;
}

.card-rename-trigger {
  display: inline-flex;
  width: 36px;
  height: 36px;
  flex: 0 0 36px;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 1px solid #e0e3e9;
  border-radius: 50%;
  background: #fff;
  color: #737b8a;
  cursor: pointer;
  transition: border-color 150ms ease, background 150ms ease, color 150ms ease;
}

.card-rename-trigger:hover,
.card-rename-trigger:focus-visible {
  border-color: #c4b5fd;
  background: #f7f4ff;
  color: #6d28d9;
}

.card-rename-trigger svg {
  width: 16px;
  height: 16px;
  fill: none;
  stroke: currentColor;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 1.8;
}

.status-pill {
  display: inline-flex;
  min-height: 26px;
  align-items: center;
  padding: 0 9px;
  border-radius: 999px;
  background: #f0f1f4;
  color: #5f6674;
  font-size: 0.65rem;
  font-weight: 750;
}

.status-pill.is-ready {
  background: #e8f7ef;
  color: #067647;
}

.status-pill.is-processing {
  background: #f0eafe;
  color: #6d28d9;
}

.status-pill.is-attention {
  background: #fff1df;
  color: #a14b08;
}

.card-copy {
  align-self: center;
}

.card-rename-form {
  display: grid;
  flex: 1;
  align-content: center;
  gap: 9px;
}

.card-rename-form > p:first-child {
  margin: 0 0 3px;
  color: #7c3aed;
  font-size: 0.7rem;
  font-weight: 750;
  letter-spacing: 0.09em;
  text-transform: uppercase;
}

.card-rename-form label {
  color: #51596a;
  font-size: 0.72rem;
  font-weight: 700;
}

.card-rename-form input {
  width: 100%;
  min-height: 46px;
  padding: 0 12px;
  border: 1px solid #a78bfa;
  border-radius: 10px;
  background: #fff;
  color: #202433;
  font: inherit;
  font-size: 0.94rem;
  font-weight: 700;
  box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.1);
}

.card-rename-error {
  margin: 0;
  color: #b42318;
  font-size: 0.72rem;
}

.card-rename-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 3px;
}

.card-rename-actions button {
  min-height: 40px;
  padding: 0 14px;
  border: 1px solid #d8dbe3;
  border-radius: 9px;
  background: #fff;
  color: #353b4a;
  font: inherit;
  font-size: 0.76rem;
  font-weight: 720;
  cursor: pointer;
}

.card-rename-actions button[type='submit'] {
  border-color: #6d28d9;
  background: #6d28d9;
  color: #fff;
}

.card-rename-actions button:disabled {
  opacity: 0.55;
  cursor: default;
}

.card-copy h3 {
  margin: 0;
  color: #202433;
  font-size: 1.25rem;
  letter-spacing: -0.025em;
  line-height: 1.25;
}

.collection-card dl {
  display: flex;
  gap: 24px;
  margin: 18px 0;
}

.collection-card dl div {
  display: flex;
  align-items: baseline;
  gap: 6px;
}

.collection-card dt {
  color: #858c9a;
  font-size: 0.68rem;
}

.collection-card dd {
  margin: 0;
  color: #2f3442;
  font-size: 0.86rem;
  font-weight: 750;
}

.collection-card footer {
  align-self: end;
  padding-top: 16px;
  border-top: 1px solid #eceef2;
}

.collection-card footer span {
  max-width: 70%;
  color: #757d8d;
  font-size: 0.72rem;
  line-height: 1.45;
}

.collection-card footer strong {
  color: #6021c8;
  font-size: 0.73rem;
  white-space: nowrap;
}

.index-state {
  display: grid;
  min-height: 340px;
  align-content: center;
  justify-items: center;
  padding: 32px;
  border: 1px solid #e4e7ed;
  border-radius: 18px;
  background: #fff;
  text-align: center;
}

.index-state h3,
.index-state p {
  margin: 7px 0;
}

.index-state p {
  max-width: 420px;
  color: #697184;
  font-size: 0.8rem;
  line-height: 1.55;
}

.index-state > span {
  color: #8b5cf6;
  font-size: 1.5rem;
}

.index-state a,
.index-state button {
  margin-top: 12px;
}

.state-mark {
  width: 28px;
  height: 28px;
  border: 3px solid #e5ddfa;
  border-top-color: #7c3aed;
  border-radius: 50%;
  animation: spin 0.9s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@media (prefers-reduced-motion: reduce) {
  .state-mark { animation: none; }
  .collection-card { transition: none; }
}

@media (max-width: 760px) {
  .collections-index-page {
    padding: 6px 16px calc(40px + env(safe-area-inset-bottom));
  }

  .index-header {
    display: grid;
    margin-bottom: 24px;
  }

  .explore-link {
    width: 100%;
  }

  .collection-grid {
    grid-template-columns: 1fr;
  }

  .collection-card {
    min-height: 240px;
  }
}

@media (max-width: 420px) {
  .list-heading p {
    display: none;
  }

  .collection-card footer {
    align-items: end;
  }

  .collection-card footer span {
    max-width: 62%;
  }
}
</style>
